/* ============================================================
   NLO THEME — product.js
   Variant selection, image gallery, add-to-cart
   ============================================================ */

'use strict';

class NLOProduct {
  constructor(el) {
    this.el = el;
    this.form = el.querySelector('.product-form');
    this.variantInput = el.querySelector('[name="id"]');
    this.sizeButtons = el.querySelectorAll('.variant-btn--size');
    this.colorSwatches = el.querySelectorAll('.variant-btn--color');
    this.priceEl = el.querySelector('.product-price__current');
    this.comparePriceEl = el.querySelector('.product-price__compare');
    this.addBtn = el.querySelector('.product-atc-btn');
    this.addBtnText = el.querySelector('.product-atc-btn__text');
    this.galleryMain = el.querySelector('.product-gallery__main');
    this.galleryThumbs = el.querySelectorAll('.product-gallery__thumb');
    this.availabilityEl = el.querySelector('.product-availability');

    this.productData = null;
    try {
      const scriptEl = el.querySelector('[data-product-json]');
      if (scriptEl) this.productData = JSON.parse(scriptEl.textContent);
    } catch (e) {}

    this.currentVariantId = this.variantInput?.value;
    this.init();
  }

  init() {
    this.sizeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.sizeButtons.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        this.resolveVariant();
      });
    });

    this.colorSwatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        this.colorSwatches.forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');
        this.resolveVariant();
      });
    });

    this.galleryThumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.setGalleryImage(i));
    });

    this.form?.addEventListener('submit', e => {
      e.preventDefault();
      this.addToCart();
    });
  }

  resolveVariant() {
    if (!this.productData) return;
    const selectedSize  = this.el.querySelector('.variant-btn--size.is-active')?.dataset.value;
    const selectedColor = this.el.querySelector('.variant-btn--color.is-active')?.dataset.value;

    const variant = this.productData.variants.find(v => {
      const hasSize  = !selectedSize  || v.option1 === selectedSize  || v.option2 === selectedSize  || v.option3 === selectedSize;
      const hasColor = !selectedColor || v.option1 === selectedColor || v.option2 === selectedColor || v.option3 === selectedColor;
      return hasSize && hasColor;
    }) || this.productData.variants[0];

    if (!variant) return;
    this.currentVariantId = variant.id;
    if (this.variantInput) this.variantInput.value = variant.id;

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());

    // Update price
    const price = this.formatMoney(variant.price);
    const comparePrice = variant.compare_at_price > variant.price ? this.formatMoney(variant.compare_at_price) : null;
    if (this.priceEl) this.priceEl.textContent = price;
    if (this.comparePriceEl) {
      this.comparePriceEl.textContent = comparePrice || '';
      this.comparePriceEl.style.display = comparePrice ? '' : 'none';
    }

    // Update availability
    const available = variant.available;
    if (this.addBtn) {
      this.addBtn.disabled = !available;
      if (this.addBtnText) this.addBtnText.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
    }
    if (this.availabilityEl) {
      this.availabilityEl.textContent = available ? 'In Stock' : 'Sold Out';
      this.availabilityEl.className = `product-availability product-availability--${available ? 'in' : 'out'}`;
    }
  }

  setGalleryImage(index) {
    const images = this.el.querySelectorAll('.product-gallery__main-img');
    images.forEach((img, i) => img.classList.toggle('is-active', i === index));
    this.galleryThumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  }

  async addToCart() {
    if (!this.currentVariantId) return;
    const btn = this.addBtn;
    const btnText = this.addBtnText;
    const originalText = btnText?.textContent || 'ADD TO CART';

    btn?.setAttribute('disabled', 'true');
    if (btnText) btnText.textContent = 'ADDING...';

    try {
      await window.NLOCart?.addItem({ id: parseInt(this.currentVariantId, 10), quantity: this.getQuantity() });
      if (btnText) btnText.textContent = 'ADDED!';
      // Animate cart icon
      document.querySelectorAll('.header-cart-icon').forEach(icon => {
        icon.classList.remove('cart-icon-success');
        void icon.offsetWidth;
        icon.classList.add('cart-icon-success');
      });
      setTimeout(() => {
        if (btnText) btnText.textContent = originalText;
        btn?.removeAttribute('disabled');
      }, 1800);
      window.NLOCart?.open();
    } catch (err) {
      if (btnText) btnText.textContent = err.message || 'ERROR';
      setTimeout(() => {
        if (btnText) btnText.textContent = originalText;
        btn?.removeAttribute('disabled');
      }, 2500);
    }
  }

  getQuantity() {
    const qtyInput = this.el.querySelector('.product-qty-input');
    return qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
  }
}

document.querySelectorAll('.product-template').forEach(el => new NLOProduct(el));

// Quantity stepper
document.querySelectorAll('.qty-stepper').forEach(stepper => {
  const input = stepper.querySelector('.product-qty-input');
  stepper.querySelector('.qty-stepper__minus')?.addEventListener('click', () => {
    const val = parseInt(input.value, 10);
    if (val > 1) input.value = val - 1;
  });
  stepper.querySelector('.qty-stepper__plus')?.addEventListener('click', () => {
    const val = parseInt(input.value, 10);
    input.value = val + 1;
  });
});

// Quick add on collection cards
document.querySelectorAll('.product-card__atc').forEach(btn => {
  btn.addEventListener('click', async e => {
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = '...';
    try {
      await window.NLOCart?.addItem({ id: parseInt(variantId, 10), quantity: 1 });
      btn.textContent = 'ADDED!';
      document.querySelectorAll('.cart-count-badge').forEach(badge => {
        badge.classList.remove('bounce');
        void badge.offsetWidth;
        badge.classList.add('bounce');
      });
      window.NLOCart?.open();
    } catch (err) {
      btn.textContent = 'ERROR';
    } finally {
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2000);
    }
  });
});
